/**
 * Dietary reasoning for venue menus.
 *
 * Rally never lets a model improvise whether a place works for someone's
 * allergy or diet. A keyword lexicon infers likely allergens and dietary tags
 * from dish names and descriptions, and every venue explanation says it rests
 * on the published menu, not the kitchen.
 */

/** The nine major US food allergens (FALCPA + FASTER Act). */
export const ALLERGENS = ['milk', 'eggs', 'fish', 'shellfish', 'tree_nuts', 'peanuts', 'wheat', 'soy', 'sesame'] as const;
export type Allergen = (typeof ALLERGENS)[number];

export const DIETS = ['vegetarian', 'vegan', 'gluten_free', 'dairy_free', 'halal', 'kosher'] as const;
export type Diet = (typeof DIETS)[number];

export type DietaryTag = Diet | 'spicy' | 'contains_alcohol';

export const ALLERGEN_LABELS: Record<Allergen, string> = {
  milk: 'dairy',
  eggs: 'eggs',
  fish: 'fish',
  shellfish: 'shellfish',
  tree_nuts: 'tree nuts',
  peanuts: 'peanuts',
  wheat: 'wheat',
  soy: 'soy',
  sesame: 'sesame',
};

export const DIET_LABELS: Record<Diet, string> = {
  vegetarian: 'vegetarian',
  vegan: 'vegan',
  gluten_free: 'gluten-free',
  dairy_free: 'dairy-free',
  halal: 'halal',
  kosher: 'kosher',
};

/** Ingredient keywords that imply an allergen. Matched on word boundaries, case-insensitive. */
const LEXICON: Record<Allergen, string[]> = {
  milk: [
    'milk', 'cheese', 'queso', 'cotija', 'crema', 'cream', 'butter', 'ghee', 'yogurt', 'yoghurt',
    'paneer', 'mozzarella', 'parmesan', 'cheddar', 'feta', 'ricotta', 'mascarpone', 'burrata',
    'latte', 'whey', 'custard', 'raita', 'malai', 'alfredo', 'ice cream', 'buttermilk', 'brie',
    'gouda', 'bechamel', 'tzatziki', 'lassi', 'kulfi', 'gelato', 'panna cotta', 'risotto',
  ],
  eggs: ['egg', 'eggs', 'mayo', 'mayonnaise', 'aioli', 'meringue', 'custard', 'omelette', 'frittata', 'brioche', 'hollandaise', 'carbonara', 'flan', 'tamago', 'tiramisu'],
  fish: ['fish', 'salmon', 'tuna', 'cod', 'halibut', 'trout', 'anchovy', 'anchovies', 'sardine', 'mackerel', 'snapper', 'fish sauce', 'dashi', 'worcestershire', 'caesar', 'sashimi', 'hilsa'],
  shellfish: ['shrimp', 'prawn', 'prawns', 'crab', 'lobster', 'scallop', 'scallops', 'clam', 'clams', 'mussel', 'mussels', 'oyster', 'oysters', 'calamari', 'squid'],
  tree_nuts: ['almond', 'almonds', 'cashew', 'cashews', 'walnut', 'walnuts', 'pecan', 'pecans', 'pistachio', 'pistachios', 'hazelnut', 'hazelnuts', 'macadamia', 'praline', 'marzipan', 'pesto', 'korma', 'baklava', 'pine nut', 'pine nuts'],
  peanuts: ['peanut', 'peanuts', 'satay', 'groundnut', 'pad thai', 'kung pao', 'mole'],
  wheat: [
    'wheat', 'flour', 'bread', 'bun', 'buns', 'tortilla', 'flour tortilla', 'pasta', 'noodle', 'noodles', 'naan', 'paratha',
    'roti', 'croissant', 'pastry', 'pie', 'cake', 'cookie', 'brownie', 'pizza', 'dumpling', 'dumplings', 'breaded', 'battered',
    'tempura', 'panko', 'couscous', 'seitan', 'soy sauce', 'crouton', 'croutons', 'sandwich', 'burger', 'wrap', 'burrito',
    'quesadilla', 'churro', 'churros', 'waffle', 'focaccia', 'samosa', 'beer', 'malt', 'baguette', 'sourdough', 'toast',
    'udon', 'ramen', 'gnocchi', 'lasagna', 'ravioli', 'spaghetti', 'tagliatelle', 'pappardelle', 'linguine', 'rigatoni',
    'pita', 'tiramisu', 'crostini', 'bruschetta', 'fried chicken', 'katsu', 'gyoza', 'farro', 'barley', 'bulgur', 'breadcrumbs',
  ],
  soy: ['soy', 'soya', 'tofu', 'edamame', 'miso', 'tempeh', 'soy sauce', 'tamari', 'teriyaki', 'hoisin'],
  sesame: ['sesame', 'tahini', 'hummus', 'halva', 'furikake', "za'atar"],
};

/** Phrases that negate an allergen ("dairy-free", "nut free", "gluten free"). */
const NEGATIONS: Array<[RegExp, Allergen[]]> = [
  [/\b(dairy|milk)[-\s]?free\b/i, ['milk']],
  [/\begg[-\s]?free\b/i, ['eggs']],
  [/\bnut[-\s]?free\b/i, ['tree_nuts', 'peanuts']],
  [/\bpeanut[-\s]?free\b/i, ['peanuts']],
  [/\b(gluten|wheat)[-\s]?free\b/i, ['wheat']],
  [/\bsoy[-\s]?free\b/i, ['soy']],
  [/\bvegan\b/i, ['milk', 'eggs', 'fish', 'shellfish']],
  [/\b(oat|almond|soy|coconut) milk\b/i, ['milk']],
];

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const COMPILED: Array<[Allergen, RegExp]> = (Object.keys(LEXICON) as Allergen[]).map((allergen) => [
  allergen,
  new RegExp(`\\b(?:${LEXICON[allergen].map(escapeRegex).join('|')})\\b`, 'i'),
]);

/** Allergens implied by free text (a dish name plus description). */
export function inferAllergens(text: string): Allergen[] {
  const negated = new Set<Allergen>();
  let scrubbed = text;
  for (const [pattern, allergens] of NEGATIONS) {
    if (pattern.test(scrubbed)) {
      allergens.forEach((a) => negated.add(a));
      scrubbed = scrubbed.replace(new RegExp(pattern.source, 'gi'), ' ');
    }
  }
  // Wheat-free lookalikes: drop them before the generic "tortilla"/"noodle"/"bread" rules see them.
  scrubbed = scrubbed.replace(/\b(corn tortillas?|rice noodles?|rice vermicelli|glass noodles?|rice paper|gluten[- ]free (bread|bun|pasta|noodles?))\b/gi, ' ');
  const found: Allergen[] = [];
  for (const [allergen, pattern] of COMPILED) {
    if (!negated.has(allergen) && pattern.test(scrubbed)) found.push(allergen);
  }
  return found;
}

const MEAT = /\b(chicken|beef|pork|lamb|goat|mutton|turkey|bacon|ham|sausage|chorizo|carnitas|pastor|barbacoa|carne|brisket|(?<!(?:cauliflower|mushroom|tofu|seitan|eggplant|celeriac|cabbage) )steak|duck|veal|prosciutto|pepperoni|salami|kacchi|biryani|kebab|kabab|short rib|ribs|meatball|bolognese|pancetta|guanciale)\b/i;

/** Dietary tags that can be inferred with reasonable safety from allergens and text. */
export function inferDietary(text: string, allergens: Allergen[]): DietaryTag[] {
  const tags = new Set<DietaryTag>();
  const lower = text.toLowerCase();
  const seafood = allergens.includes('fish') || allergens.includes('shellfish');
  const animal = allergens.some((a) => a === 'milk' || a === 'eggs') || seafood || MEAT.test(lower);
  if (/\bvegan\b/.test(lower) && !animal) tags.add('vegan').add('vegetarian');
  else if (/\b(vegetarian|veggie)\b/.test(lower) && !MEAT.test(lower) && !seafood) tags.add('vegetarian');
  if (/\b(gluten|wheat)[-\s]?free\b/.test(lower) && !allergens.includes('wheat')) tags.add('gluten_free');
  if (/\b(dairy|milk)[-\s]?free\b/.test(lower) && !allergens.includes('milk')) tags.add('dairy_free');
  if (/\bhalal\b/.test(lower)) tags.add('halal');
  if (/\bkosher\b/.test(lower)) tags.add('kosher');
  if (/\b(spicy|habanero|jalape[nñ]o|chipotle|sriracha|vindaloo|chili|chilli)\b/.test(lower)) tags.add('spicy');
  if (/\b(beer|wine|margarita|sangria|mezcal|tequila|cocktail|sake|whiskey|vodka|rum)\b/.test(lower)) tags.add('contains_alcohol');
  return [...tags];
}

export type MenuDish = {
  name: string;
  description: string;
  priceCents: number;
  course: 'starter' | 'main' | 'dessert' | 'drink';
  /** Allergens the venue declared; when absent they are inferred from the text. */
  allergens?: Allergen[];
  dietary?: DietaryTag[];
};

export type AnalyzedDish = MenuDish & { allergens: Allergen[]; dietary: DietaryTag[]; basis: 'declared' | 'inferred' };

/** Fill a dish's allergens and dietary tags. Declared facts are kept; claims that contradict inferred animal products are dropped. */
export function analyzeDish(dish: MenuDish): AnalyzedDish {
  const text = `${dish.name}. ${dish.description}`;
  const declared = Array.isArray(dish.allergens);
  const allergens = declared ? [...dish.allergens!] : inferAllergens(text);
  const dietary = [...new Set([...(dish.dietary ?? []), ...inferDietary(text, allergens)])].filter(
    (tag) =>
      !(tag === 'vegan' && allergens.some((a) => a === 'milk' || a === 'eggs' || a === 'fish' || a === 'shellfish')) &&
      !((tag === 'vegetarian' || tag === 'vegan') && (MEAT.test(text) || allergens.includes('fish') || allergens.includes('shellfish'))) &&
      !(tag === 'gluten_free' && allergens.includes('wheat')) &&
      !(tag === 'dairy_free' && allergens.includes('milk')),
  );
  return { ...dish, allergens, dietary, basis: declared ? 'declared' : 'inferred' };
}

/** Does a dish suit a diet? Vegan dishes also suit vegetarians. */
export function dishSuitsDiet(dish: AnalyzedDish, diet: Diet): boolean {
  if (diet === 'vegetarian') return dish.dietary.includes('vegetarian') || dish.dietary.includes('vegan');
  if (diet === 'gluten_free') return dish.dietary.includes('gluten_free') || !dish.allergens.includes('wheat');
  if (diet === 'dairy_free') return dish.dietary.includes('dairy_free') || !dish.allergens.includes('milk');
  return dish.dietary.includes(diet);
}

/** Is a dish free of an allergen? Tree-nut and peanut allergies are treated together as "nut". */
export function dishAvoids(dish: AnalyzedDish, allergen: Allergen): boolean {
  return !dish.allergens.includes(allergen);
}
